import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Une vidéo de fond qui reboucle SANS la coupure sèche de l'attribut `loop`.
 *
 * Le problème signalé par Emeni : `<video loop>` saute de la dernière image à la première. Sur un
 * travelling — le ciel étoilé — les deux images n'ont rien à voir, et le raccord se voit comme un
 * à-coup. Aucun réglage de la balise vidéo ne corrige ça : la coupure est dans le fichier, dont la
 * fin ne rejoint pas le début.
 *
 * La solution retenue est le fondu enchaîné, la même que celle des fonds vidéo du web : DEUX
 * lectures du même fichier se relaient. Quand la première approche de sa fin, la seconde redémarre
 * à zéro sous elle, et l'on croise les opacités pendant `FONDU` secondes. L'œil traverse un fondu
 * d'une seconde au lieu d'un saut, et le mouvement du travelling le rend quasi invisible.
 *
 * Ce que ça NE coûTE PAS : un second téléchargement. Les deux balises pointent le même fichier, que
 * le navigateur sert depuis son cache. Seul le décodage est doublé, et uniquement pendant le fondu.
 *
 * Deux autres pistes ont été écartées :
 *   - refabriquer un fichier « aller-retour » (la séquence puis son miroir) : le raccord serait
 *     parfait, mais le fichier doublerait de durée et de poids, très au-delà des 25 Mio par fichier
 *     qu'accepte Cloudflare — et la version actuelle est déjà à 22,7 Mio ;
 *   - un fondu au noir en fin de boucle : ce n'est plus un saut, mais c'est un clignotement.
 */

/** Durée du croisement. Assez long pour que l'œil ne voie pas de raccord, assez court pour ne pas
 *  amputer la boucle d'une part visible de sa durée. */
const FONDU = 1.2;

interface Props {
    src: string;
    /** Image de repli, affichée tant que la vidéo n'a pas démarré. */
    poster?: string;
    className?: string;
}

export const VideoEnBoucle: React.FC<Props> = ({ src, poster, className = '' }) => {
    const videoA = useRef<HTMLVideoElement>(null);
    const videoB = useRef<HTMLVideoElement>(null);
    const [actif, setActif] = useState(0);
    // Lus dans un gestionnaire d'événement : un état React y serait figé à sa valeur de rendu.
    const actifRef = useRef(0);
    const enTransition = useRef(false);

    const lecteur = useCallback((i: number) => (i === 0 ? videoA.current : videoB.current), []);

    useEffect(() => {
        actifRef.current = 0;
        enTransition.current = false;
        setActif(0);
        const premier = videoA.current;
        if (premier) {
            premier.muted = true;
            premier.currentTime = 0;
            premier.play().catch(() => { /* lecture auto refusée : l'image de repli reste visible */ });
        }
        // La seconde attend son tour, arrêtée sur sa première image.
        const second = videoB.current;
        if (second) { second.muted = true; second.pause(); }
    }, [src]);

    /** Appelé plusieurs fois par seconde par la balise active : on n'agit qu'à l'approche de la fin. */
    const surAvancement = (i: number) => () => {
        if (i !== actifRef.current || enTransition.current) return;
        const courante = lecteur(i);
        const suivante = lecteur(1 - i);
        if (!courante || !suivante) return;
        const duree = courante.duration;
        if (!duree || !isFinite(duree) || duree - courante.currentTime > FONDU) return;

        enTransition.current = true;
        suivante.currentTime = 0;
        suivante.play().catch(() => { /* idem : on garde l'image en place plutôt qu'un écran noir */ });
        actifRef.current = 1 - i;
        setActif(1 - i);

        // Une fois le croisement terminé, la sortante est remise au repos : elle ne consomme plus de
        // décodage, et repartira de zéro au tour suivant.
        window.setTimeout(() => {
            enTransition.current = false;
            try { courante.pause(); } catch { /* la balise a pu être démontée entre-temps */ }
        }, FONDU * 1000);
    };

    const commun = 'absolute inset-0 w-full h-full object-cover';
    const transition = `opacity ${FONDU}s linear`;

    return (
        <>
            <video
                ref={videoA}
                src={src}
                poster={poster}
                autoPlay muted playsInline preload="auto"
                onTimeUpdate={surAvancement(0)}
                className={`${commun} ${className}`}
                style={{ opacity: actif === 0 ? 1 : 0, transition }}
            />
            <video
                ref={videoB}
                src={src}
                muted playsInline preload="auto"
                onTimeUpdate={surAvancement(1)}
                className={`${commun} ${className}`}
                style={{ opacity: actif === 1 ? 1 : 0, transition }}
            />
        </>
    );
};
